// [AstraChat] Transporte de ÁUDIO por WebSocket (PCM16 16kHz mono) para a chamada
// AstraCall embutida no dashboard.
//
// POR QUÊ: a tela de chamada era WebRTC-only. Em redes/servidores onde o WebRTC
// (UDP) não fecha o ICE, a chamada conectava mas ficava MUDA (o RTP não chegava ao
// navegador). O AstraCalls expõe um transporte alternativo de áudio por WebSocket
// (sobre 443/wss, passa em qualquer proxy):
//   wss://<host>/api/sessions/{sid}/calls/{id}/ws   (subprotocolo "pcm16")
// auth por ?apiKey= na URL. É ÁUDIO-ONLY (vídeo continua só no WebRTC).
//
// Reusa o padrão de AudioWorklets do useGeminiLive.js (captura em pedaços a 16kHz;
// playback com fila). Diferença: os frames no fio são Int16 LE BINÁRIOS (não base64).

const RATE = 16000;
const FRAME = 512; // 512 amostras @16kHz = 32ms por frame (~31 msgs/s), baixa latência

// Captura: acumula FRAME amostras Float32 e manda pro main thread.
const CAPTURE_WORKLET = `
class AstraCallCapture extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(${FRAME}); this.i = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      for (let n = 0; n < ch.length; n++) {
        this.buf[this.i++] = ch[n];
        if (this.i >= this.buf.length) { this.port.postMessage(this.buf.slice()); this.i = 0; }
      }
    }
    return true;
  }
}
registerProcessor('astracall-ws-capture', AstraCallCapture);
`;

// Playback: fila de Float32; toca sequencialmente, preenche com silêncio quando vazia.
const PLAYBACK_WORKLET = `
class AstraCallPlayback extends AudioWorkletProcessor {
  constructor() {
    super();
    this.queue = [];
    this.off = 0;
    this.port.onmessage = e => {
      if (e.data === 'flush') { this.queue = []; this.off = 0; }
      else if (e.data instanceof Float32Array) this.queue.push(e.data);
    };
  }
  process(_i, outputs) {
    const out = outputs[0][0];
    if (!out) return true;
    let k = 0;
    while (k < out.length && this.queue.length) {
      const cur = this.queue[0];
      const copy = Math.min(out.length - k, cur.length - this.off);
      for (let n = 0; n < copy; n++) out[k++] = cur[this.off++];
      if (this.off >= cur.length) { this.queue.shift(); this.off = 0; }
    }
    while (k < out.length) out[k++] = 0;
    return true;
  }
}
registerProcessor('astracall-ws-playback', AstraCallPlayback);
`;

const blobUrl = code =>
  URL.createObjectURL(new Blob([code], { type: 'application/javascript' }));

// Abre a ponte de áudio por WebSocket. Retorna { close, setMuted } — ou lança se o
// WS não abrir em ~8s (o chamador cai de volta pro WebRTC / mostra erro).
export async function openWsAudio({ wsUrl, micStream, onEvent } = {}) {
  const ws = new WebSocket(wsUrl, 'pcm16');
  ws.binaryType = 'arraybuffer';

  const Ctx = window.AudioContext || window.webkitAudioContext;
  const inCtx = new Ctx({ sampleRate: RATE });
  const outCtx = new Ctx({ sampleRate: RATE });

  let closed = false;
  let muted = false;
  let src = null;
  let captureNode = null;
  let playbackNode = null;
  let sink = null;

  const close = () => {
    if (closed) return;
    closed = true;
    try {
      if (captureNode) captureNode.port.onmessage = null;
    } catch (e) {
      /* noop */
    }
    try {
      captureNode?.disconnect();
    } catch (e) {
      /* noop */
    }
    try {
      sink?.disconnect();
    } catch (e) {
      /* noop */
    }
    try {
      src?.disconnect();
    } catch (e) {
      /* noop */
    }
    try {
      playbackNode?.disconnect();
    } catch (e) {
      /* noop */
    }
    try {
      inCtx.close();
    } catch (e) {
      /* noop */
    }
    try {
      outCtx.close();
    } catch (e) {
      /* noop */
    }
    try {
      ws.close();
    } catch (e) {
      /* noop */
    }
  };

  try {
    await outCtx.audioWorklet.addModule(blobUrl(PLAYBACK_WORKLET));
    await inCtx.audioWorklet.addModule(blobUrl(CAPTURE_WORKLET));

    playbackNode = new AudioWorkletNode(outCtx, 'astracall-ws-playback');
    playbackNode.connect(outCtx.destination);

    src = inCtx.createMediaStreamSource(micStream);
    captureNode = new AudioWorkletNode(inCtx, 'astracall-ws-capture');
    captureNode.port.onmessage = ev => {
      if (muted || ws.readyState !== WebSocket.OPEN) return;
      const f = ev.data; // Float32Array
      const pcm = new Int16Array(f.length);
      for (let i = 0; i < f.length; i += 1) {
        const s = Math.max(-1, Math.min(1, f[i]));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      ws.send(pcm.buffer);
    };
    // captura precisa estar no grafo pra ser bombeada; ligamos num gain ZERO
    // (sem eco no alto-falante) que vai até o destination.
    src.connect(captureNode);
    sink = inCtx.createGain();
    sink.gain.value = 0;
    captureNode.connect(sink);
    sink.connect(inCtx.destination);

    ws.onmessage = ev => {
      if (typeof ev.data === 'string') {
        // frames de texto = eventos JSON (roster/estado/participante). No grupo
        // (events=1) entregamos pro chamador; sem onEvent, ignora (áudio-only 1:1).
        if (onEvent) {
          let parsed = ev.data;
          try {
            parsed = JSON.parse(ev.data);
          } catch (e) {
            /* texto cru */
          }
          onEvent(parsed);
        }
        return;
      }
      const pcm = new Int16Array(ev.data);
      const f = new Float32Array(pcm.length);
      for (let i = 0; i < pcm.length; i += 1) f[i] = pcm[i] / 0x8000;
      playbackNode.port.postMessage(f);
    };
    ws.onclose = () => close();
    ws.onerror = () => close();

    await inCtx.resume?.().catch(() => {});
    await outCtx.resume?.().catch(() => {});

    // espera o WS abrir (ou falhar) antes de dar a ponte por pronta
    await new Promise((resolve, reject) => {
      if (ws.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }
      const t = setTimeout(() => reject(new Error('ws-timeout')), 8000);
      ws.addEventListener(
        'open',
        () => {
          clearTimeout(t);
          resolve();
        },
        { once: true }
      );
      ws.addEventListener(
        'error',
        () => {
          clearTimeout(t);
          reject(new Error('ws-error'));
        },
        { once: true }
      );
    });
  } catch (e) {
    close();
    throw e;
  }

  return {
    close,
    setMuted(m) {
      muted = !!m;
    },
  };
}
